import * as React from "react";

import { AlertDialog } from "../../components/ui/alert-dialog";
import { Box } from "../../components/ui/box";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import {
  Command,
  CommandDialog,
  CommandDialogPopup,
  CommandDialogTrigger,
  CommandFooter,
  CommandGroup,
  CommandGroupLabel,
  CommandInput,
  CommandItem,
  CommandList,
  CommandPanel,
} from "../../components/ui/command";
import { EmptyState } from "../../components/ui/empty";
import { Icon } from "../../components/ui/icon";
import { Kbd } from "../../components/ui/kbd";
import { useKeyboardHints } from "../../hooks/use-keyboard-hints";
import { Text } from "../../components/ui/text";

import type {
  BulkAction,
  BulkActionConfirm,
  BulkActionOptionPicker,
  BulkActionPopover,
  BulkActionRun,
} from "./bulk-actions-bar-types";
import {
  selectionCountLabel,
  ActionIcon,
  BarButton,
  bulkActionGroupOf,
  ConfirmActionPopup,
  executeBulkAction,
  resolveSuccessMessage,
  type ActionButtonSharedProps,
} from "./bulk-actions-shared";

// The More-actions command dialog: the third presentation of the bulk-action
// grammar (beside the desktop strip and the mobile sheet). The strip carries
// the two or three actions worth a dedicated button; everything else is
// discoverable here — a searchable command list over EVERY visible action,
// where picking one either runs it (direct), asks first (confirm, nested over
// the command popup), or steps the panel into the action's own body
// (popover / option-picker) under a chip chain that names the pending command.

/** The label an action reads as in the dialog list. */
function dialogLabelOf<TData>(action: BulkAction<TData>): string {
  return action.dialogLabel ?? action.label;
}

/** The step chip drops the list label's trailing ellipsis: the step is no
 *  longer pending once its body is open. */
function stepChipLabel(label: string): string {
  return label.replace(/(\.{3}|…)$/, "");
}

/** An action whose pick opens a second step instead of firing. */
type StepAction<TData> =
  | BulkActionPopover<TData>
  | BulkActionOptionPicker<TData>;

function isStepAction<TData>(
  action: BulkAction<TData>,
): action is StepAction<TData> {
  return action.type === "popover" || action.type === "option-picker";
}

export type BulkActionsMoreDialogProps<TData> =
  ActionButtonSharedProps<TData> & {
    /** Every visible action — strip actions included, so the dialog is the
     *  searchable superset, not just the overflow. */
    actions: BulkAction<TData>[];
  };

export function BulkActionsMoreDialog<TData>({
  actions,
  selectedRows,
  displayCount,
  selectionNoun,
  selectAllGroups,
  loadingActionId,
  onClearSelection,
  setLoadingActionId,
}: BulkActionsMoreDialogProps<TData>): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  // The footer is a legend of keys and nothing else — it stands down whole
  // where no keyboard can press them.
  const keyboardHints = useKeyboardHints();
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [confirmId, setConfirmId] = React.useState<string | null>(null);
  const closeActionRef = React.useRef<
    (BulkActionRun<TData>) | null
  >(null);
  const closeMessageRef = React.useRef<React.ReactNode>(undefined);

  const findAction = (id: string | null): BulkAction<TData> | null =>
    id != null ? (actions.find((a) => a.id === id) ?? null) : null;
  const active = findAction(activeId);
  const activeStep = active != null && isStepAction(active) ? active : null;
  const confirmCandidate = findAction(confirmId);
  const confirmAction =
    confirmCandidate?.type === "confirm"
      ? (confirmCandidate as BulkActionConfirm<TData>)
      : null;

  /** A popover body's draft-on-close: fires when the step is left — back to
   *  the list, or the dialog closing — mirroring the popover and mobile-sheet
   *  presentations of the same contract. */
  const runDeferred = React.useCallback(() => {
    const actionFn = closeActionRef.current;
    if (!actionFn || !activeStep) return;
    const message = closeMessageRef.current;
    closeActionRef.current = null;
    closeMessageRef.current = undefined;
    executeBulkAction({
      actionId: activeStep.id,
      actionFn: () => actionFn(selectedRows),
      label: activeStep.label,
      count: displayCount,
      onClearSelection,
      setLoadingActionId,
      successMessage: message,
    });
  }, [
    activeStep,
    selectedRows,
    displayCount,
    onClearSelection,
    setLoadingActionId,
  ]);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) {
        // Reset per open, not on close: clearing on close would reshuffle the
        // list under the popup's exit transition.
        setQuery("");
        setActiveId(null);
        setConfirmId(null);
      } else {
        runDeferred();
      }
    },
    [runDeferred],
  );

  /** Close without the deferred hook — an explicit run already consumed it. */
  const close = React.useCallback(() => {
    closeActionRef.current = null;
    closeMessageRef.current = undefined;
    setOpen(false);
  }, []);

  const run = React.useCallback(
    (
      action: BulkAction<TData>,
      actionFn: BulkActionRun<TData>,
      successMessage?: React.ReactNode,
      quiet?: boolean,
    ) => {
      close();
      executeBulkAction({
        actionId: action.id,
        actionFn: () => actionFn(selectedRows),
        label: action.label,
        count: displayCount,
        onClearSelection,
        setLoadingActionId,
        successMessage,
        quiet,
      });
    },
    [close, selectedRows, displayCount, onClearSelection, setLoadingActionId],
  );

  const handleSelect = React.useCallback(
    (action: BulkAction<TData>) => {
      if (action.type === "direct") {
        run(
          action,
          (rows) => action.onAction(rows),
          resolveSuccessMessage(action.successMessage, selectedRows, displayCount),
          action.quiet,
        );
        return;
      }
      if (action.type === "confirm") {
        setConfirmId(action.id);
        return;
      }
      setQuery("");
      setActiveId(action.id);
    },
    [run, selectedRows, displayCount],
  );

  const handleBack = React.useCallback(() => {
    runDeferred();
    setQuery("");
    setActiveId(null);
  }, [runDeferred]);

  const setCloseAction = React.useCallback(
    (
      actionFn: (BulkActionRun<TData>) | null,
      successMessage?: React.ReactNode,
    ) => {
      closeActionRef.current = actionFn;
      closeMessageRef.current = successMessage;
    },
    [],
  );

  const commit = React.useCallback(
    (
      actionFn: BulkActionRun<TData>,
      successMessage?: React.ReactNode,
    ) => {
      if (!activeStep) return;
      run(activeStep, actionFn, successMessage);
    },
    [activeStep, run],
  );

  const q = query.trim().toLowerCase();
  const matches = actions.filter((a) =>
    dialogLabelOf(a).toLowerCase().includes(q),
  );
  // Two headed groups, not a separator: "Set or change" (the actions that
  // pick a value for the selection) then "Apply an action" (the verbs that
  // run as-is), so the break between runs carries meaning. Destructive verbs
  // close the second group whatever order the consumer declared them in,
  // as the destructive glyphs close the strip before its More trigger.
  // A lone group lists flat:
  // a heading with nothing to tell apart is noise.
  const setMatches = matches.filter((a) => bulkActionGroupOf(a) === "set");
  const applyMatches = matches.filter((a) => bulkActionGroupOf(a) === "apply");
  const applyOrdered = [
    ...applyMatches.filter((a) => !a.destructive),
    ...applyMatches.filter((a) => a.destructive),
  ];
  const groups = [
    { id: "set", heading: "Set or change", items: setMatches },
    { id: "apply", heading: "Apply an action", items: applyOrdered },
  ].filter((g) => g.items.length > 0);

  const renderItem = (action: BulkAction<TData>): React.ReactElement => {
    const reason = action.disabledReason?.(selectedRows, selectAllGroups);
    return (
      <CommandItem
        disabled={reason != null}
        key={action.id}
        onClick={() => handleSelect(action)}
        value={action.id}
      >
        <Box
          align="center"
          className={cn(
            "w-full",
            action.destructive && !reason
              ? "text-destructive-foreground"
              : undefined,
          )}
          direction="row"
          gap="sm"
        >
          <ActionIcon
            color={action.destructive && !reason ? undefined : "muted"}
            icon={action.icon}
          />
          {dialogLabelOf(action)}
          {reason != null ? (
            // The block, trailing on the row — the list's counterpart of the
            // strip button's tooltip.
            <Text className="ms-auto" size="xs" variant="muted">
              {reason}
            </Text>
          ) : null}
        </Box>
      </CommandItem>
    );
  };

  const rootStep = (
    <Command key="root" mode="none" onValueChange={setQuery} value={query}>
      <CommandInput
        placeholder="Type a command or search..."
        startAddon={null}
      />
      <CommandPanel>
        <CommandList>
          {matches.length === 0 ? (
            <Box align="center" justify="center" paddingY="md">
              <EmptyState size="xs" title="No matching actions" />
            </Box>
          ) : (
            groups.map((group) =>
              groups.length > 1 ? (
                <CommandGroup key={group.id}>
                  <CommandGroupLabel>{group.heading}</CommandGroupLabel>
                  {group.items.map(renderItem)}
                </CommandGroup>
              ) : (
                <React.Fragment key={group.id}>
                  {group.items.map(renderItem)}
                </React.Fragment>
              ),
            )
          )}
        </CommandList>
      </CommandPanel>
      {/* `relative` lifts the legend above the popup's two-tone veil (an
          absolutely-positioned ::before that would otherwise wash it); the
          ink override then reads at true strength on the veiled zone. */}
      {keyboardHints && (
      <CommandFooter className="relative px-4 text-popover-foreground/60">
        <Box align="center" direction="row" gap="md">
          <Box align="center" direction="row" gap="xs">
            <Kbd className="text-popover-foreground/80">↑</Kbd>
            <Kbd className="text-popover-foreground/80">↓</Kbd>
            <Text size="xs">Navigate</Text>
          </Box>
          <Box align="center" direction="row" gap="xs">
            <Kbd className="text-popover-foreground/80">Enter</Kbd>
            <Text size="xs">Run action</Text>
          </Box>
        </Box>
        <Box align="center" direction="row" gap="xs">
          <Kbd className="text-popover-foreground/80">Esc</Kbd>
          <Text size="xs">Close</Text>
        </Box>
      </CommandFooter>
      )}
    </Command>
  );

  const optionStep = (action: BulkActionOptionPicker<TData>) => {
    const optionMatches = action.options.filter((o) =>
      o.label.toLowerCase().includes(q),
    );
    return (
      <Command
        key={action.id}
        mode="none"
        onValueChange={setQuery}
        value={query}
      >
        <CommandInput placeholder="Search..." startAddon={null} />
        <CommandPanel>
          <CommandList>
            {optionMatches.length === 0 ? (
              <Box align="center" justify="center" paddingY="md">
                <EmptyState size="xs" title="No matching options" />
              </Box>
            ) : (
              optionMatches.map((option) => (
                <CommandItem
                  key={option.id}
                  onClick={() =>
                    run(
                      action,
                      (rows) => action.onAction(rows, option.id),
                      resolveSuccessMessage(
                        action.successMessage,
                        selectedRows,
                        displayCount,
                        option.id,
                      ),
                    )
                  }
                  value={option.id}
                >
                  <Box align="center" direction="row" gap="sm">
                    <ActionIcon color="muted" icon={option.icon} />
                    {option.label}
                  </Box>
                </CommandItem>
              ))
            )}
          </CommandList>
        </CommandPanel>
      </Command>
    );
  };

  const popoverStep = (action: BulkActionPopover<TData>) => (
    <CommandPanel key={action.id}>
      {/* The full min-height-0 chain: the popup's height cap shrinks the
          panel, the panel shrinks this column, and the column shrinks the
          action body — whose own scroll region (an overflow-hidden shell has
          no automatic flex minimum) then does the scrolling, keeping a pinned
          footer visible instead of clipping it against the popup edge. */}
      <Box
        direction="column"
        grow
        minHeight={0}
        padding={
          action.contentProps?.padding === "none"
            ? undefined
            : (action.contentProps?.padding ?? "sm")
        }
      >
        {action.content({
          selectedRows,
          selectAllGroups,
          commit,
          setCloseAction,
        })}
      </Box>
    </CommandPanel>
  );

  return (
    <CommandDialog
      onOpenChange={(next, details) => {
        // Inside a step, Escape is "back": cancel the close, pop to the
        // action list (running any drafted close action) — the second press
        // then closes the dialog.
        if (!next && activeStep != null && details.reason === "escape-key") {
          details.cancel();
          handleBack();
          return;
        }
        handleOpenChange(next);
      }}
      open={open}
    >
      {/* A labeled trigger — "More" with the ellipsis — at the strip's end,
          so the overflow reads as a word, not a mystery glyph. */}
      <CommandDialogTrigger
        render={
          <BarButton disabled={loadingActionId !== null}>
            <Icon name="ellipsis" size="sm" />
            More
          </BarButton>
        }
      />
      <CommandDialogPopup
        aria-label="More bulk actions"
        // A step hosts an action's own body (a selector with its search,
        // list and pinned footer), which reads cramped under the root list's
        // content cap — the step trades it for a taller one. The swap is a
        // snap, not an eased layout change, per the house motion rule.
        className={
          activeStep != null ? "max-h-[min(37.5rem,80dvh)]" : undefined
        }
      >
        {/* The chip chain, on the popup's veiled chrome zone above the input:
            the selection token, then — inside a step — the pending command.
            The selection chip clears and closes; the step chip goes back. */}
        <Box
          align="center"
          direction="row"
          gap="xs"
          paddingTop="base"
          paddingX="base"
          shrink={false}
        >
          <Button
            onClick={() => {
              close();
              onClearSelection();
            }}
            shape="square"
            size="xs"
            variant="outline"
          >
            {selectionCountLabel(displayCount, selectionNoun)}
            <Icon name="x" size="xs" />
          </Button>
          {activeStep != null && (
            <>
              <Icon color="muted" name="chevron-right" size="xs" />
              <Button
                aria-label={`Back — ${stepChipLabel(dialogLabelOf(activeStep))}`}
                onClick={handleBack}
                shape="square"
                size="xs"
                variant="outline"
              >
                <ActionIcon color="muted" icon={activeStep.icon} size="xs" />
                {stepChipLabel(dialogLabelOf(activeStep))}
              </Button>
            </>
          )}
        </Box>
        {activeStep == null
          ? rootStep
          : activeStep.type === "option-picker"
            ? optionStep(activeStep)
            : popoverStep(activeStep)}
        {confirmAction != null && (
          <AlertDialog
            onOpenChange={(next) => {
              if (!next) setConfirmId(null);
            }}
            open
          >
            <ConfirmActionPopup
              action={confirmAction}
              onConfirm={() => {
                setConfirmId(null);
                run(
                  confirmAction,
                  (rows) => confirmAction.onAction(rows),
                  resolveSuccessMessage(
                    confirmAction.successMessage,
                    selectedRows,
                    displayCount,
                  ),
                );
              }}
              selectedRows={selectedRows}
            />
          </AlertDialog>
        )}
      </CommandDialogPopup>
    </CommandDialog>
  );
}
